import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { Modal } from 'react-bootstrap';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import {
  useCreateDataChangeRequestMutation,
  useValidateDataChangeRequestMutation,
  useEmployeeRecordFieldsQuery,
  RequestFormFragment,
  RequestForm_EmployeeFragment,
  ObjectFieldValueFragment,
} from '../../../__generated__/graphql';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { useToasts } from '../../../../../context/Toast';
import { useUserInfo } from '../../../context/UserInfo';
import { StepSelectRequestForm } from './StepSelectRequestForm';
import { StepSelectEmployee } from './StepSelectEmployee';
import { StepReasonFiles } from './StepReasonFiles';
import { employeeObjectApiName, RequestFormData, StepType } from './types';
import { NavigationButtons } from './NavigationButtons';
import { PositionChangeComponent } from './components/positionChange';
import { SalaryChangeStepsWrapper } from './components/salaryChange';
import { StepEmployeeDetailsComponent } from './components/employeeDetails/StepEmployeeDetailsComponent';
import { ServerValidationAlert } from './components/ServerValidationAlert';
import { Stack, Spinner } from 'react-bootstrap';
import { mapToCreateDataChangeRequestInput } from './mappings';
import {
  createExtraFieldForEmployeeReference,
  getAllSteps,
  getEmployeeDefaultValues,
  getEmployeeSalary,
  isStepValid,
  mapToRecordField,
  validateOnSubmit,
} from './logic';
import { StepReview } from './StepReview';
import { StepObjectCreate } from './StepObjectCreate';
import { StepSelectRequestFormSkeleton } from './StepSelectRequestFormSkeleton';
import { ExtraFieldsAreaContentSimpleRef } from '../../ExtraFields/ExtraFieldsArea/ExtraFieldsAreaContentSimple';
import { ExtraFieldInternalValue } from '../../ExtraFields/extraFieldInternalValue';
import { EmployeeRelatedListRecord } from '../../EmployeeData/types';
import { buildRecordFieldsOverride } from './buildRecordFieldsOverride';

type Props = {
  show: boolean;
  showAllRequests: boolean;
  loading: boolean;
  requestForms: readonly RequestFormFragment[];
  employees: readonly RequestForm_EmployeeFragment[];
  onHide: () => void;
  isSelectEmployeeStepVisible: boolean;
  mode?: 'create' | 'edit';
  // The related-list record being edited. Field metadata and prefill values
  // are derived from this record + the selected Request Form's allowed fields.
  editingRecord?: EmployeeRelatedListRecord;
  // API name of the object being edited (e.g., "flair__Child__c").
  editObjectType?: string;
  // Label of the object being edited (e.g., the related list label "Children").
  // Used as the EmployeeDetails step heading in edit mode.
  editObjectLabel?: string;
};

export const RequestForm: React.FC<React.PropsWithChildren<Props>> = ({
  show,
  requestForms,
  employees,
  onHide,
  loading: initialLoading,
  isSelectEmployeeStepVisible,
  mode = 'create',
  editingRecord,
  editObjectType,
  editObjectLabel,
}) => {
  const t = useNamespacedTranslation('changeRequests.requestForm');
  const { addSuccess, addError } = useToasts();
  const errorHandler = useMutationErrorHandler();
  const { id: currentUserId, name: currentUserName } = useUserInfo();
  const isEditMode = mode === 'edit';
  const isFormPreselected = isEditMode && requestForms.length === 1;
  const preselectedFormId = isFormPreselected ? requestForms[0].Id : undefined;
  const buildInitialFormData = useCallback((): RequestFormData => {
    if (isEditMode) {
      return {
        requestFormId: preselectedFormId ?? null,
        employeeId: currentUserId,
        editingRecordId: editingRecord?.id ?? null,
        fields: {},
      };
    }
    return {};
  }, [isEditMode, preselectedFormId, editingRecord, currentUserId]);

  const [currentStep, setCurrentStep] = useState(0);
  const [validating, setValidating] = useState(false);
  const [formData, setFormData] = useState<RequestFormData>(() =>
    buildInitialFormData(),
  );

  const prefilledFormIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (show && isEditMode) {
      prefilledFormIdRef.current = undefined;
      setFormData(buildInitialFormData());
      setCurrentStep(0);
    }
  }, [show, isEditMode, buildInitialFormData]);
  const [serverValidationError, setServerValidationError] = useState<
    string | null
  >(null);
  const stepObjectCreateRef = useRef<ExtraFieldsAreaContentSimpleRef>(null);

  const [createDataChangeRequest, { loading: isSubmittingRequest }] =
    useCreateDataChangeRequestMutation();

  const [validateDataChangeRequest] = useValidateDataChangeRequestMutation();

  const selectedRequestForm = useMemo(() => {
    return requestForms.find((form) => form.Id === formData.requestFormId);
  }, [requestForms, formData.requestFormId]);

  const employeeObjectFields = useMemo(() => {
    return (
      selectedRequestForm?.objectsFields.find(
        (field) => field.objectName === employeeObjectApiName,
      )?.fields || []
    );
  }, [selectedRequestForm]);

  const recordFieldsOverride: ObjectFieldValueFragment[] | undefined =
    useMemo(() => {
      if (!isEditMode || !editingRecord || !editObjectType) return undefined;
      const allowedFields =
        selectedRequestForm?.objectsFields.find(
          (f) => f.objectName === editObjectType,
        )?.fields ?? [];
      return buildRecordFieldsOverride(
        editingRecord,
        allowedFields,
        editObjectType,
      );
    }, [isEditMode, editingRecord, editObjectType, selectedRequestForm]);

  const {
    data: employeeRecordFieldsData,
    loading: employeeRecordFieldsLoading,
  } = useEmployeeRecordFieldsQuery({
    variables: {
      employeeId: formData.employeeId || '',
      recordType: employeeObjectApiName,
      fields: employeeObjectFields,
    },
    skip:
      !!recordFieldsOverride ||
      !formData.requestFormId ||
      !formData.employeeId ||
      selectedRequestForm?.flair__Data_Change_Object_Name__c !==
        employeeObjectApiName,
  });

  const employeeRecordFields: readonly ObjectFieldValueFragment[] =
    useMemo(() => {
      if (recordFieldsOverride) {
        return recordFieldsOverride;
      }
      return employeeRecordFieldsData?.recordFields || [];
    }, [recordFieldsOverride, employeeRecordFieldsData]);

  // When the user picks a different form (multi-form edit case), clear the
  // pending fields diff so the new form starts with no edits. Defaults for
  // the form UI come from recordFieldsOverride via getEmployeeDefaultValues,
  // and the watcher emits only the fields the user actually changes.
  // Guarded by a ref so spurious re-renders for the same form don't clobber
  // the user's in-progress edits.
  useEffect(() => {
    if (
      !isEditMode ||
      !editingRecord ||
      !editObjectType ||
      !selectedRequestForm
    )
      return;
    if (prefilledFormIdRef.current === selectedRequestForm.Id) return;
    prefilledFormIdRef.current = selectedRequestForm.Id;
    setFormData((prev) => ({ ...prev, fields: {} }));
  }, [isEditMode, editingRecord, editObjectType, selectedRequestForm]);

  const employeesPlusMe = [
    ...employees,
    { Id: currentUserId, Name: currentUserName },
  ];
  const selectedEmployee = employeesPlusMe.find(
    (emp) => emp.Id === formData.employeeId,
  );

  const selectedForm = requestForms.find(
    (form) => form.Id === formData.requestFormId,
  );

  const handleSelectForm = (formId: string | null) => {
    const selectedForm = requestForms.find((form) => form.Id === formId);
    const assignCurrentEmployeeToRequestForm = !isSelectEmployeeStepVisible;
    const employeeDefaultExtraFieldValue = assignCurrentEmployeeToRequestForm
      ? createExtraFieldForEmployeeReference(
          {
            id: currentUserId,
            name: currentUserName,
          },
          selectedForm,
        )
      : null;

    setFormData((prev) => ({
      ...prev,
      requestFormId: formId,
      employeeId: assignCurrentEmployeeToRequestForm
        ? currentUserId
        : prev.employeeId,
      extraFields: employeeDefaultExtraFieldValue
        ? [employeeDefaultExtraFieldValue]
        : [],
    }));
  };

  const handleEmployeeSelect = (employeeId: string | null) => {
    if (!employeeId?.length) {
      return;
    }
    const employeeName = employees.find((emp) => emp.Id === employeeId)?.Name;
    const employeeDefaultExtraFieldValue = createExtraFieldForEmployeeReference(
      {
        id: employeeId,
        name: employeeName ?? '',
      },
      selectedForm,
    );
    setFormData((prev) => ({
      ...prev,
      employeeId: employeeId,
      reason: '',
      files: [],
      fields: {},
      // Drop the previous employee's salary draft so the salary step re-seeds
      // the new employee's amount / pay band instead of treating the stale
      // value as a restored draft.
      salaryChange: undefined,
      extraFields: employeeDefaultExtraFieldValue
        ? [employeeDefaultExtraFieldValue]
        : [],
    }));
    setCurrentStep((step) => step + 1);
  };

  const availableSteps = useMemo(() => {
    return getAllSteps(
      selectedRequestForm,
      isSelectEmployeeStepVisible,
      isFormPreselected,
      isEditMode,
    );
  }, [
    selectedRequestForm,
    isSelectEmployeeStepVisible,
    isFormPreselected,
    isEditMode,
  ]);

  const handleStepBack = () => {
    if (currentStep === 0) {
      handleHide();
      return;
    }
    setServerValidationError(null);
    setCurrentStep((prev) => prev - 1);
  };

  const doServerSideValidation = async (): Promise<boolean> => {
    if (!selectedRequestForm || !formData.employeeId) {
      return false;
    }
    setValidating(true);
    setServerValidationError(null);
    try {
      const input = await mapToCreateDataChangeRequestInput(
        currentUserId,
        employeeRecordFields,
        formData,
        selectedRequestForm,
      );

      const result = await validateDataChangeRequest({
        variables: { input },
      });
      if (result.data?.requestForm?.validateCreateDataChangeRequest?.isValid) {
        setServerValidationError(null);
        return true;
      } else {
        const validationError =
          result.data?.requestForm?.validateCreateDataChangeRequest?.error;
        setServerValidationError(validationError ?? '');
        return false;
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setValidating(false);
    }
    return false;
  };

  const handleStepNext = () => {
    if (currentStep === availableSteps.length - 1) {
      handleFinalSubmit();
      return;
    }
    if (
      currentStepType === StepType.ObjectCreate &&
      stepObjectCreateRef.current &&
      selectedRequestForm?.extraFieldInfos
    ) {
      setValidating(true);
      stepObjectCreateRef.current
        .triggerValidation()
        .then(async (valid) => {
          if (!valid) {
            setValidating(false);
            return;
          }
          if (!selectedRequestForm || !formData.employeeId) {
            setValidating(false);
            return;
          }
          const isValid = await doServerSideValidation();
          if (isValid) {
            setCurrentStep((step) => step + 1);
          }
        })
        .catch((error) => {
          errorHandler(error);
          setValidating(false);
        });

      return;
    }
    if (currentStepType === StepType.EmployeeDetails) {
      doServerSideValidation().then((isValid) => {
        if (isValid) {
          setCurrentStep((step) => step + 1);
          return;
        }
      });
      return;
    }
    setCurrentStep((step) => step + 1);
  };

  const handleFinalSubmit = async () => {
    const error = validateOnSubmit(availableSteps, formData);
    if (error) {
      addError(error);
      return;
    }

    if (!selectedRequestForm) {
      addError('Request form is required');
      return;
    }

    try {
      const input = await mapToCreateDataChangeRequestInput(
        currentUserId,
        employeeRecordFields,
        formData,
        selectedRequestForm,
      );

      await createDataChangeRequest({
        variables: {
          input,
        },
      });
      addSuccess(t('notifications.success'));
      setCurrentStep(0);
      setFormData(buildInitialFormData());
      onHide();
    } catch (error) {
      errorHandler(error);
    }
  };

  const currentStepType = availableSteps[currentStep];

  const isCurrentStepValid = useMemo(() => {
    return isStepValid(currentStepType, selectedRequestForm, formData);
  }, [currentStepType, selectedRequestForm, formData]);

  const handleEmployeeDetailsChange = useCallback(
    (fieldData: { [key: string]: string }) => {
      setFormData((prev) => ({ ...prev, fields: fieldData }));
    },
    [],
  );

  const handleObjectCreateChange = useCallback(
    (
      extraFields: ExtraFieldInternalValue[],
      recordTypeName: string | undefined,
    ) => {
      setFormData((prev) => ({
        ...prev,
        extraFields: extraFields,
        recordTypeName: recordTypeName,
      }));
    },
    [],
  );

  const currentSalary = useMemo(() => {
    return getEmployeeSalary(employeeRecordFields);
  }, [employeeRecordFields]);

  const defaultValues = useMemo(() => {
    return getEmployeeDefaultValues(employeeRecordFields, formData.fields);
  }, [employeeRecordFields, formData.fields]);

  const memoizedPositionData = useMemo(() => {
    return formData.positionChange
      ? {
          effectiveDate: formData.positionChange.effectiveDate,
          unknownEndDate: formData.positionChange.unknownEndDate,
          endDate: formData.positionChange.endDate,
        }
      : undefined;
  }, [formData.positionChange]);

  const renderStep = () => {
    const recordFields = employeeRecordFields.map(mapToRecordField);

    if (currentStepType === StepType.SelectForm) {
      return (
        <StepSelectRequestForm
          requestForms={requestForms}
          onSubmit={handleStepNext}
          onCancel={onHide}
          selectedFormId={formData.requestFormId ?? null}
          onFormSelect={handleSelectForm}
        />
      );
    }

    if (currentStepType === StepType.SelectEmployee) {
      return (
        <StepSelectEmployee
          employees={employees}
          selectedEmployeeId={formData.employeeId ?? null}
          onEmployeeSelect={handleEmployeeSelect}
        />
      );
    }

    if (currentStepType === StepType.ReasonFiles) {
      if (!formData.employeeId || !selectedRequestForm) {
        return null;
      }
      const hideReason = selectedRequestForm?.flair__Hide_Reason__c === true;
      const hideAttachment =
        selectedRequestForm?.flair__Hide_Attachment__c === true;
      const isReasonMandatory =
        !hideReason &&
        (selectedRequestForm?.flair__Mandatory_Reason__c || false);
      const isUploadMandatory =
        !hideAttachment &&
        (selectedRequestForm?.flair__Mandatory_Upload__c || false);
      return (
        <StepReasonFiles
          reason={formData.reason || ''}
          setReason={(newReason) =>
            setFormData((prev) => ({ ...prev, reason: newReason }))
          }
          selectedFiles={formData.files || []}
          setSelectedFiles={(newFiles) =>
            setFormData((prev) => ({ ...prev, files: newFiles }))
          }
          isReasonMandatory={isReasonMandatory}
          hideReason={hideReason}
          hideAttachment={hideAttachment}
          isUploadMandatory={isUploadMandatory}
        />
      );
    }

    if (currentStepType === StepType.ObjectCreate) {
      if (!selectedRequestForm) {
        throw new Error('Selected request form should be defined at that step');
      }
      // we should not allow employee (and manager too) to change flair__Employee_Field_Api_Name__c
      const extraFieldInfos = selectedRequestForm.extraFieldInfos?.map(
        (field) => {
          const isEmployeeField =
            field.fieldApiName ===
            selectedRequestForm.flair__Employee_Field_Api_Name__c;
          return {
            ...field,
            ...(isEmployeeField && { disabled: true }),
          };
        },
      );
      return (
        <StepObjectCreate
          requestFormId={selectedRequestForm.Id}
          recordTypes={selectedRequestForm.recordTypes ?? null}
          ref={stepObjectCreateRef}
          values={formData.extraFields ?? []}
          extraFieldInfos={extraFieldInfos ?? null}
          recordTypeName={formData.recordTypeName ?? null}
          onChange={handleObjectCreateChange}
        />
      );
    }

    if (currentStepType === StepType.Position) {
      return (
        <Stack gap={0}>
          <div className="mb-4">
            <h4>{t('steps.positionChange')}</h4>
            <p className="text-muted">
              {t('steps.position_changeDescription')}
            </p>
          </div>
          <PositionChangeComponent
            selectedEmployeeId={formData.employeeId!}
            onChange={(data) => {
              return setFormData((prev) => ({ ...prev, positionChange: data }));
            }}
            value={formData.positionChange}
          />
        </Stack>
      );
    }

    if (currentStepType === StepType.Salary) {
      if (!formData.employeeId) {
        throw new Error('Employee ID is required for salary change');
      }
      return (
        <Stack gap={0}>
          <div className="mb-4">
            <h4>{t('steps.salaryChange')}</h4>
            <p className="text-muted">{t('steps.salary_changeDescription')}</p>
          </div>
          <SalaryChangeStepsWrapper
            selectedEmployeeId={formData.employeeId}
            currentSalary={currentSalary}
            currency="€"
            onChange={(data) =>
              setFormData((prev) => ({ ...prev, salaryChange: data }))
            }
            value={formData.salaryChange}
            positionChangeData={memoizedPositionData}
          />
        </Stack>
      );
    }

    if (currentStepType === StepType.EmployeeDetails) {
      const editStepTitle = isEditMode
        ? editObjectLabel ?? selectedRequestForm?.Name
        : undefined;
      const editStepDescription = isEditMode
        ? selectedRequestForm?.flair__Information__c ?? ''
        : undefined;
      return (
        <StepEmployeeDetailsComponent
          recordFields={recordFields}
          defaultValues={defaultValues}
          onChange={handleEmployeeDetailsChange}
          title={editStepTitle}
          description={editStepDescription}
        />
      );
    }

    if (currentStepType === StepType.Review) {
      return (
        <StepReview
          formData={formData}
          selectedRequestForm={selectedForm || null}
          recordFields={recordFields}
          selectedEmployee={selectedEmployee}
          isSelectEmployeeStepVisible={isSelectEmployeeStepVisible}
        />
      );
    }

    return null;
  };

  const handleHide = () => {
    setCurrentStep(0);
    setFormData(buildInitialFormData());
    setServerValidationError(null);
    onHide();
  };

  const handleRemoveEmployee = () => {
    setFormData((form) => ({
      requestFormId: form.requestFormId,
    }));
    setCurrentStep(0);
    setServerValidationError(null);
  };

  const selectedEmployeeName = selectedEmployee?.Name;
  const isLoading =
    initialLoading ||
    employeeRecordFieldsLoading ||
    isSubmittingRequest ||
    validating;

  const isEmployeeInHeaderVisible =
    currentStep > 0 && isSelectEmployeeStepVisible && selectedEmployeeName;

  return (
    <Modal show={show} onHide={handleHide} centered size="lg" scrollable>
      <Modal.Header closeButton={!isSubmittingRequest}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            <Modal.Title className="fs-2">
              {currentStepType === StepType.SelectForm
                ? t('title')
                : selectedRequestForm?.Name || t('title')}
            </Modal.Title>
            <div className="text-muted fs-5 mt-1">
              {getStepDescription(currentStepType, t)}
            </div>
          </div>
          {isEmployeeInHeaderVisible && (
            <div className="me-1">
              <span className="badge bg-primary d-flex align-items-center gap-3 fs-5 py-3 px-4">
                {selectedEmployeeName}
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  style={{ fontSize: '1rem' }}
                  onClick={handleRemoveEmployee}
                  disabled={isLoading}
                />
              </span>
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        {employeeRecordFieldsLoading && <Loading />}
        <ServerValidationAlert error={serverValidationError} />
        {renderStep()}
        {initialLoading && <StepSelectRequestFormSkeleton />}
      </Modal.Body>
      <Modal.Footer>
        <NavigationButtons
          onBack={handleStepBack}
          onNext={handleStepNext}
          disableNext={!isCurrentStepValid || validating || isLoading}
          disableBack={isLoading}
          currentStep={currentStep}
          totalSteps={availableSteps.length}
          isSubmitting={isSubmittingRequest || validating}
        />
      </Modal.Footer>
    </Modal>
  );
};

function getStepDescription(
  stepType: StepType,
  t: (key: string) => string,
): string {
  if (stepType === StepType.SelectForm) return t('steps.stepOnePlaceholder');
  if (stepType === StepType.SelectEmployee)
    return t('steps.stepTwoPlaceholder');
  if (stepType === StepType.ObjectCreate) return t('steps.objectCreate');
  if (stepType === StepType.ReasonFiles) return t('steps.reasonFiles');
  if (stepType === StepType.Position) return t('steps.positionChange');
  if (stepType === StepType.Salary) return t('steps.salaryChange');
  if (stepType === StepType.EmployeeDetails) return t('steps.employeeDetails');
  if (stepType === StepType.Review) return t('steps.stepThreePlaceholder');
  return '';
}

const Loading: React.FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <Stack gap={0}>
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
      </div>
    </Stack>
  );
};
